// src/pages/ELearning/MyAssignmentsPage.tsx
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getMyAssignments } from '../../api/client';
import { Loader2, ClipboardList, Clock, AlertTriangle, CheckCircle, PlayCircle, Calendar } from 'lucide-react';

interface Assignment {
  _id: string;
  test?: { _id: string; title: string; description?: string; image?: string; category?: string };
  assignedBy?: { name?: string; email?: string };
  assignedByName?: string;
  deadline: string;
  notes?: string;
  status: 'pending' | 'in_progress' | 'completed' | 'overdue' | 'cancelled';
  completedAt?: string;
  createdAt: string;
}

const MyAssignmentsPage: React.FC = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState<Assignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        const data = await getMyAssignments();
        setItems(data.items || []);
      } catch (e: any) {
        console.error(e);
        setError(e.response?.data?.error || 'Не удалось загрузить назначения');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const getDaysLeft = (deadline: string): number => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const d = new Date(deadline);
    d.setHours(0, 0, 0, 0);
    return Math.round((d.getTime() - now.getTime()) / 86400000);
  };

  const getStatusBadge = (a: Assignment) => {
    const days = getDaysLeft(a.deadline);
    if (a.status === 'completed') {
      return { text: 'Прйден', color: '#10b981', bg: 'rgba(16,185,129,0.15)', icon: <CheckCircle size={14} /> };
    }
    if (a.status === 'overdue' || days < 0) {
      return { text: `Просрочен на ${Math.abs(days)} дн.`, color: '#e74c3c', bg: 'rgba(231,76,60,0.15)', icon: <AlertTriangle size={14} /> };
    }
    if (days === 0) {
      return { text: 'Сегодня последний день', color: '#e74c3c', bg: 'rgba(231,76,60,0.15)', icon: <AlertTriangle size={14} /> };
    }
    if (days <= 3) {
      return { text: `Осталось ${days} дн.`, color: '#f59e0b', bg: 'rgba(245,158,11,0.15)', icon: <Clock size={14} /> };
    }
    return { text: `Осталось ${days} дн.`, color: '#2575fc', bg: 'rgba(37,117,252,0.12)', icon: <Clock size={14} /> };
  };

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
        <ClipboardList size={32} color="#2575fc" />
        <h1 style={{ fontSize: 28, fontWeight: 700, margin: 0, color: '#1a1a2e' }}>Мои назначения</h1>
      </div>

      {loading && (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
          <Loader2 size={40} style={{ animation: 'spin 1s linear infinite' }} color="#2575fc" />
        </div>
      )}

      {!loading && error && (
        <div style={{ padding: 20, background: 'rgba(231,76,60,0.1)', borderRadius: 12, color: '#e74c3c' }}>{error}</div>
      )}

      {!loading && !error && items.length === 0 && (
        <div style={{ textAlign: 'center', padding: 60, background: 'rgba(255,255,255,0.5)', borderRadius: 20, border: '1px dashed rgba(37,117,252,0.3)' }}>
          <ClipboardList size={48} color="#2575fc" style={{ opacity: 0.5 }} />
          <h3 style={{ marginTop: 16, color: '#666' }}>Вам пока не назначено ни одного теста</h3>
          <p style={{ color: '#999', fontSize: 14 }}>Когда HR назначит вам тест, он появится здесь</p>
        </div>
      )}

      {!loading && !error && items.length > 0 && (
        <div style={{ display: 'grid', gap: 16 }}>
          {items.map(a => {
            const badge = getStatusBadge(a);
            const test: any = a.test || {};
            return (
              <div key={a._id} style={{ background: 'rgba(255,255,255,0.7)', backdropFilter: 'blur(8px)', borderRadius: 20, padding: 20, border: '1px solid rgba(255,255,255,0.5)', boxShadow: '0 4px 16px rgba(0,0,0,0.05)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                <div style={{ flex: '1 1 300px' }}>
                  <h3 style={{ fontSize: 18, fontWeight: 700, margin: 0, marginBottom: 6, color: '#1a1a2e' }}>{test.title || 'Тест'}</h3>
                  {test.description && <p style={{ fontSize: 13, color: '#666', margin: 0, marginBottom: 8 }}>{test.description}</p>}
                  <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', fontSize: 13, color: '#666', alignItems: 'center' }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      <Calendar size={14} /> Дедлайн: <strong>{new Date(a.deadline).toLocaleDateString('ru-RU')}</strong>
                    </span>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 10px', borderRadius: 10, background: badge.bg, color: badge.color, fontWeight: 600, fontSize: 12 }}>
                      {badge.icon} {badge.text}
                    </span>
                  </div>
                  {a.notes && <p style={{ fontSize: 12, color: '#888', fontStyle: 'italic', margin: '8px 0 0 0' }}>💬 {a.notes}</p>}
                </div>
                <button onClick={() => navigate('/app/elearning/tests/' + test._id)} style={{ padding: '10px 20px', background: a.status === 'completed' ? '#95a5a6' : '#2575fc', color: '#fff', border: 'none', borderRadius: 12, fontWeight: 600, fontSize: 14, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <PlayCircle size={16} /> {a.status === 'completed' ? 'Пройти снова' : 'Пройти тест'}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
};

export default MyAssignmentsPage;
